import { useEffect, useState } from 'react';
import './App.css';
import LoginForm from './components/LoginForm';
import { MainLayout } from './components/layouts/MainLayout';
import { SectionRouter } from './components/layouts/SectionRouter';
import { useAuth } from './hooks/useAuth';
import { useNavigation } from './hooks/useNavigation';
import type { ApiResponse } from './types/api';

interface AppInfo {
    isTestServer: boolean;
    displayVersion: string;
}

function App() {
    const { currentUser, isAuthenticated, authLoading, handleLogin, handleLogout } = useAuth();
    const { currentSection, navigationItems, handleSectionChange } = useNavigation(currentUser);
    const [isTestServer, setIsTestServer] = useState(false);
    const [displayVersion, setDisplayVersion] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const baseTitle = document.title.replace(/^\[TEST\]\s*/, '');

        fetch('/api/app-info')
            .then(response => response.ok ? response.json() as Promise<ApiResponse<AppInfo>> : null)
            .then(response => {
                if (!isMounted || !response?.data) {
                    return;
                }

                setIsTestServer(response.data.isTestServer);
                setDisplayVersion(response.data.displayVersion);
                document.title = response.data.isTestServer ? `[TEST] ${baseTitle}` : baseTitle;
            })
            .catch(() => {
                document.title = baseTitle;
            });

        return () => {
            isMounted = false;
            document.title = baseTitle;
        };
    }, []);

    const banner = isTestServer ? (
        <div className="test-server-banner" role="status">
            Вы находитесь на тестовом сервере
        </div>
    ) : null;

    if (authLoading) {
        return (
            <div className={`app-shell ${isTestServer ? 'has-test-banner' : ''}`}>
                {banner}
                <div className="container mt-4 text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Загрузка...</span>
                    </div>
                    <div className="mt-2">Проверка аутентификации...</div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className={`app-shell ${isTestServer ? 'has-test-banner' : ''}`}>
                {banner}
                <LoginForm onLogin={handleLogin} />
            </div>
        );
    }

    return (
        <div className={`app-shell ${isTestServer ? 'has-test-banner' : ''}`}>
            {banner}
            <MainLayout
                currentSection={currentSection}
                currentUser={currentUser}
                onSectionChange={handleSectionChange}
                onLogout={handleLogout}
                navigationItems={navigationItems}
                displayVersion={displayVersion}
            >
                <SectionRouter currentSection={currentSection} currentUser={currentUser} />
            </MainLayout>
        </div>
    );
}

export default App;
